import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { TwoFactorCard } from "@/components/account/TwoFactorCard";
import { PhoneCard } from "@/components/account/PhoneCard";
import { Loader2, KeyRound, Mail, User as UserIcon, ShieldCheck, HardHat } from "lucide-react";

const AccountPage = () => {
  const { user, refreshSession } = useAuth();

  // Email form
  const [emailForm, setEmailForm] = useState({ current_password: "", new_email: "" });
  const [savingEmail, setSavingEmail] = useState(false);

  // Password form
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.current_password || !emailForm.new_email.trim()) {
      return toast.error("Renseignez votre mot de passe actuel et le nouvel email");
    }
    try {
      setSavingEmail(true);
      const res = await api.changeMyEmail(emailForm.current_password, emailForm.new_email.trim());
      // Met à jour la session avec le nouveau JWT (ancien token contient l'ancien email)
      refreshSession({ token: res.data?.token, user: res.data?.user });
      toast.success("Email mis à jour");
      setEmailForm({ current_password: "", new_email: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    } finally {
      setSavingEmail(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!pwdForm.current_password || !pwdForm.new_password) {
      return toast.error("Mot de passe actuel et nouveau mot de passe obligatoires");
    }
    if (pwdForm.new_password.length < 6) {
      return toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
    }
    if (pwdForm.new_password !== pwdForm.confirm) {
      return toast.error("La confirmation ne correspond pas");
    }
    try {
      setSavingPwd(true);
      const res = await api.changeMyPassword(pwdForm.current_password, pwdForm.new_password);
      if (res.data?.token) refreshSession({ token: res.data.token });
      toast.success("Mot de passe modifié");
      setPwdForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    } finally {
      setSavingPwd(false);
    }
  };

  const roleLabel = user?.role === "admin"
    ? "Administrateur"
    : user?.employee_id ? "Ouvrier / Salarié" : "Utilisateur";
  const RoleIcon = user?.role === "admin" ? ShieldCheck : user?.employee_id ? HardHat : UserIcon;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="account-page">
      <div>
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
          <UserIcon className="h-6 w-6 text-indigo-600" /> Mon compte
        </h1>
        <p className="text-sm text-muted-foreground">
          Modifiez votre adresse email de connexion et votre mot de passe. Votre compte (et toutes vos données associées) reste identique.
        </p>
      </div>

      {/* Identity card */}
      <Card data-testid="account-identity-card">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
              <RoleIcon className="h-6 w-6 text-indigo-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-muted-foreground">Connecté en tant que</div>
              <div className="text-lg font-semibold truncate" data-testid="account-current-email">
                {user?.email || "—"}
              </div>
              <div className="text-xs text-muted-foreground">{roleLabel}{user?.name ? ` · ${user.name}` : ""}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email change */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Mail className="h-5 w-5 text-indigo-600" /> Changer l'email de connexion
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitEmail} className="space-y-3">
            <div>
              <Label htmlFor="ce-pwd">Mot de passe actuel *</Label>
              <Input
                id="ce-pwd"
                type="password"
                autoComplete="current-password"
                value={emailForm.current_password}
                onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
                data-testid="change-email-current-password"
              />
            </div>
            <div>
              <Label htmlFor="ce-email">Nouvel email *</Label>
              <Input
                id="ce-email"
                type="email"
                autoComplete="email"
                placeholder="ex : nouvel-email@exemple.fr"
                value={emailForm.new_email}
                onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                data-testid="change-email-new-email"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Vous l'utiliserez à la prochaine connexion. Vos données et permissions restent inchangées.
              </p>
            </div>
            <Button type="submit" disabled={savingEmail} data-testid="change-email-submit">
              {savingEmail ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
              Mettre à jour l'email
            </Button>
          </form>
        </CardContent>
      </Card>

      <Separator />

      {/* Password change */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-indigo-600" /> Changer le mot de passe
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitPassword} className="space-y-3">
            <div>
              <Label htmlFor="cp-current">Mot de passe actuel *</Label>
              <Input
                id="cp-current"
                type="password"
                autoComplete="current-password"
                value={pwdForm.current_password}
                onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                data-testid="change-password-current"
              />
            </div>
            <div>
              <Label htmlFor="cp-new">Nouveau mot de passe *</Label>
              <Input
                id="cp-new"
                type="password"
                autoComplete="new-password"
                value={pwdForm.new_password}
                onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                data-testid="change-password-new"
              />
              <p className="text-xs text-muted-foreground mt-1">Minimum 6 caractères.</p>
            </div>
            <div>
              <Label htmlFor="cp-confirm">Confirmer le nouveau mot de passe *</Label>
              <Input
                id="cp-confirm"
                type="password"
                autoComplete="new-password"
                value={pwdForm.confirm}
                onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                data-testid="change-password-confirm"
              />
            </div>
            <Button type="submit" disabled={savingPwd} data-testid="change-password-submit">
              {savingPwd ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
              Modifier le mot de passe
            </Button>
          </form>
        </CardContent>
      </Card>

      <PhoneCard />
      <TwoFactorCard />
    </div>
  );
};

export default AccountPage;
